import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  PencilSimple,
  Trash,
  X,
  CreditCard,
  CheckCircle,
  MinusCircle,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PERMISSIONS } from "../lib/permissions";

const PAGE_SIZE = 24;

const FRECUENCIAS = [
  { value: "mensual", label: "Mensual" },
  { value: "bimensual", label: "Bimensual" },
  { value: "bimensual_impar", label: "Bimensual impar" },
  { value: "anual", label: "Anual" },
];
const DIAS = [1, 5, 10];

const emptyMandato = () => ({
  referencia: "",
  iban: "",
  titular_nombre: "",
  titular_fallero_id: "",
  tipo: "domiciliado",
  frecuencia: "anual",
  dia_pago: 1,
  fecha_firma: "",
  estado: "activo",
  notas: "",
  fallero_ids: [],
});

const formatIban = (iban) =>
  (iban || "")
    .replace(/\s+/g, "")
    .toUpperCase()
    .replace(/(.{4})/g, "$1 ")
    .trim();

export default function Mandatos() {
  const [list, setList] = useState([]);
  const [falleros, setFalleros] = useState([]);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [mandatoFalleros, setMandatoFalleros] = useState([]);
  const [freeFalleros, setFreeFalleros] = useState([]);
  const [selectedFallero, setSelectedFallero] = useState("");
  const { can } = useAuth();
  const canManage = can(PERMISSIONS.MANAGE_MANDATOS);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(t);
  }, [search]);

  const fetchAll = async () => {
    try {
      setLoading(true);

      const mandatosReq = api.get("/mandatos");

      const fallerosReq = modal
        ? api.get("/falleros")
        : Promise.resolve({ data: [] });

      const [m, f] = await Promise.all([mandatosReq, fallerosReq]);

      setList(m.data);
      setFalleros(f.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchAll();
  }, [modal]);

  const filteredMandatos = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();

    if (!q) {
      return list;
    }

    return list.filter((m) => {
      return [m.referencia, m.iban, m.titular_nombre, m.tipo]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [list, debouncedSearch]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredMandatos.length / PAGE_SIZE),
  );

  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;

    return filteredMandatos.slice(start, start + PAGE_SIZE);
  }, [filteredMandatos, page]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!modal.isEdit && (modal.data.fallero_ids || []).length === 0) {
      toast.error("Selecciona al menos un fallero");
      return;
    }
    try {
      const payload = {
        ...modal.data,
        titular_fallero_id: modal.data.titular_fallero_id || null,
        fecha_firma: modal.data.fecha_firma || null,
        dia_pago: Number(modal.data.dia_pago) || 1,
      };
      if (modal.isEdit) {
        delete payload.fallero_ids;
        await api.put(`/mandatos/${modal.data.id}`, payload);
        toast.success("Mandato actualizado");
      } else {
        await api.post("/mandatos", payload);
        toast.success("Mandato creado");
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    }
  };

  const toggleFalleroInMandato = (id) => {
    const cur = modal.data.fallero_ids || [];
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    setModal({ ...modal, data: { ...modal.data, fallero_ids: next } });
  };

  const onDelete = async (m) => {
    if (
      !window.confirm(
        `¿Eliminar mandato ${m.referencia}? Los falleros se desvincularán.`,
      )
    )
      return;
    await api.delete(`/mandatos/${m.id}`);
    toast.success("Eliminado");
    fetchAll();
  };

  const titularName = (m) => {
    if (m.titular_fallero_id) {
      const f = falleros.find((x) => x.id === m.titular_fallero_id);
      if (f) return `${f.nombre} ${f.apellidos}`;
    }
    return m.titular_nombre || "—";
  };

  return (
    <div className="p-6 md:p-10" data-testid="mandatos-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-med-muted font-semibold mb-2">
            Mandatos bancarios
          </div>
          <h1 className="font-display text-4xl font-bold text-med-text">
            Mandatos
          </h1>
          <p className="text-med-muted mt-1">
            Cada IBAN tiene un mandato; uno o varios falleros pueden
            compartirlo.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setModal({ data: emptyMandato(), isEdit: false })}
            data-testid="add-mandato-btn"
            className="inline-flex items-center gap-2 bg-med-primary hover:bg-med-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl px-5 py-2.5 font-semibold active:scale-[0.98]"
          >
            <Plus size={18} weight="bold" />
            Nuevo mandato
          </button>
        )}
      </header>

      <div className="relative mb-6">
        <MagnifyingGlass
          size={18}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-med-muted"
        />

        <input
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          placeholder="Buscar por referencia, IBAN o titular..."
          className="w-full rounded-2xl border border-med-border bg-med-surface py-3 pl-12 pr-5 outline-none transition-all focus:border-med-primary focus:ring-2 focus:ring-med-primary/20"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {loading ? (
          <div className="md:col-span-2 xl:col-span-3 bg-med-surface border border-med-border rounded-2xl p-16 text-center text-med-muted">
            Cargando mandatos...
          </div>
        ) : list.length === 0 ? (
          <div className="md:col-span-2 xl:col-span-3 bg-med-surface border border-med-border rounded-2xl p-16 text-center text-med-muted">
            Aún no hay mandatos. Crea el primero arriba.
          </div>
        ) : (
          paginated.map((m) => (
            <div
              key={m.id}
              className="bg-med-surface border border-med-border rounded-2xl p-5 flex flex-col"
              data-testid={`mandato-card-${m.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-med-secondary/10 text-med-secondary grid place-items-center">
                  <CreditCard size={20} weight="duotone" />
                </div>
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    m.estado === "activo"
                      ? "bg-[#D1FAE5] text-[#065F46]"
                      : "bg-[#E5E7EB] text-[#374151]"
                  }`}
                >
                  {m.estado === "activo" ? (
                    <CheckCircle size={12} weight="fill" />
                  ) : (
                    <MinusCircle size={12} weight="fill" />
                  )}
                  {m.estado === "activo" ? "Activo" : "Inactivo"}
                </span>
              </div>

              <div className="font-mono text-xs text-med-muted">
                {m.referencia}
              </div>
              <div className="font-display text-lg font-bold text-med-text mt-1 break-all">
                {m.tipo === "pago_en_mano"
                  ? "Pago en mano — sin IBAN"
                  : formatIban(m.iban)}
              </div>
              {m.tipo === "domiciliado" && (
                <div className="text-xs text-med-muted mt-0.5">
                  BIC: {m.bic || "—"}
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-med-border/60 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-med-muted">Titular</span>
                  <span className="font-medium text-med-text text-right">
                    {titularName(m)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-med-muted">Tipo</span>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-semibold ${
                      m.tipo === "domiciliado"
                        ? "bg-[#E0F2FE] text-[#0369A1]"
                        : "bg-[#FFEDD5] text-[#9A3412]"
                    }`}
                  >
                    {m.tipo === "domiciliado" ? "Domiciliado" : "Pago en mano"}
                  </span>
                </div>
                {m.tipo === "domiciliado" && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-med-muted">Frecuencia</span>
                      <span className="text-med-text capitalize">
                        {
                          FRECUENCIAS.find((f) => f.value === m.frecuencia)
                            ?.label
                        }
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-med-muted">Día de pago</span>
                      <span className="text-med-text">Día {m.dia_pago}</span>
                    </div>
                  </>
                )}
                {m.fecha_firma && (
                  <div className="flex justify-between">
                    <span className="text-med-muted">Firmado</span>
                    <span className="text-med-text">
                      {formatDate(m.fecha_firma)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-med-muted">Falleros vinculados</span>
                  <span className="font-semibold text-med-primary">
                    {m.num_falleros}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                {canManage ? (
                  <>
                    <button
                      onClick={async () => {
                        try {
                          const linked = await api.get(
                            `/mandatos/${m.id}/falleros`,
                          );
                          const all = await api.get("/falleros");

                          setMandatoFalleros(linked.data);
                          setFreeFalleros(
                            all.data.filter((f) => !f.mandato_id),
                          );
                          setModal({
                            data: {
                              ...m,
                              titular_fallero_id: m.titular_fallero_id || "",
                            },
                            isEdit: true,
                          });
                        } catch {
                          toast.error("No se pudieron cargar los falleros");
                        }
                      }}
                      data-testid={`edit-mandato-${m.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-med-border hover:border-med-secondary text-med-text text-sm font-medium"
                    >
                      <PencilSimple size={14} weight="duotone" />
                      Editar
                    </button>
                    <button
                      onClick={() => onDelete(m)}
                      data-testid={`delete-mandato-${m.id}`}
                      className="px-3 py-2 rounded-lg hover:bg-med-danger/10 text-med-danger"
                    >
                      <Trash size={16} weight="duotone" />
                    </button>
                  </>
                ) : (
                  <div
                    className="flex-1 text-xs text-med-muted text-center italic"
                    data-testid={`readonly-mandato-${m.id}`}
                  >
                    Solo lectura
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="rounded-xl border border-med-border px-4 py-2 text-sm disabled:opacity-40"
          >
            Anterior
          </button>

          <div className="text-sm text-med-muted">
            Página {page} de {totalPages}
          </div>

          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="rounded-xl border border-med-border px-4 py-2 text-sm disabled:opacity-40"
          >
            Siguiente
          </button>
        </div>
      )}

      {modal && (
        <div
          className="fixed inset-0 bg-med-text/40 backdrop-blur-sm grid place-items-center z-50 p-4"
          onClick={() => setModal(null)}
        >
          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-med-surface rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="mandato-modal"
          >
            <div className="sticky top-0 bg-med-surface border-b border-med-border px-6 py-4 flex items-center justify-between">
              <h3 className="font-display text-xl font-bold">
                {modal.isEdit ? "Editar mandato" : "Nuevo mandato"}
              </h3>
              <button
                type="button"
                onClick={() => setModal(null)}
                className="p-1.5 rounded-lg hover:bg-med-surface2"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Referencia <span className="text-med-primary">*</span>
                </label>
                <input
                  required
                  value={modal.data.referencia}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, referencia: e.target.value },
                    })
                  }
                  placeholder="MAND-2026-0001"
                  data-testid="mandato-referencia"
                  className="w-full font-mono rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Estado
                </label>
                <select
                  value={modal.data.estado}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, estado: e.target.value },
                    })
                  }
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                >
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </div>

              {modal.data.tipo === "domiciliado" && (
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    IBAN <span className="text-med-primary">*</span>
                  </label>
                  <input
                    required
                    value={modal.data.iban || ""}
                    onChange={(e) =>
                      setModal({
                        ...modal,
                        data: {
                          ...modal.data,
                          iban: e.target.value.toUpperCase(),
                        },
                      })
                    }
                    placeholder="ES76 2100 0418 4012 3456 7891"
                    data-testid="mandato-iban"
                    className="w-full font-mono rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                  />
                  <div className="text-xs text-med-muted mt-1">
                    BIC se calculará automáticamente al guardar.
                  </div>
                </div>
              )}
              {modal.data.tipo === "pago_en_mano" && (
                <div className="md:col-span-2 bg-med-warning/10 border border-med-warning/30 rounded-xl p-3 text-sm text-med-text">
                  Los mandatos de <strong>pago en mano</strong> no requieren
                  IBAN.
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Titular (texto libre)
                </label>
                <input
                  value={modal.data.titular_nombre}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, titular_nombre: e.target.value },
                    })
                  }
                  placeholder="Nombre del titular"
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  O elegir fallero existente
                </label>
                <select
                  value={modal.data.titular_fallero_id || ""}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: {
                        ...modal.data,
                        titular_fallero_id: e.target.value,
                      },
                    })
                  }
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                >
                  <option value="">— Ninguno —</option>
                  {falleros.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.nombre} {f.apellidos}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Tipo
                </label>
                <select
                  value={modal.data.tipo}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, tipo: e.target.value },
                    })
                  }
                  data-testid="mandato-tipo"
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                >
                  <option value="domiciliado">Domiciliado</option>
                  <option value="pago_en_mano">Pago en mano</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Fecha de firma
                </label>
                <input
                  type="date"
                  value={modal.data.fecha_firma || ""}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, fecha_firma: e.target.value },
                    })
                  }
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                />
              </div>

              {modal.data.tipo === "domiciliado" && (
                <>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                      Frecuencia
                    </label>
                    <select
                      value={modal.data.frecuencia}
                      onChange={(e) =>
                        setModal({
                          ...modal,
                          data: { ...modal.data, frecuencia: e.target.value },
                        })
                      }
                      data-testid="mandato-frecuencia"
                      className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                    >
                      {FRECUENCIAS.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                      Día de pago
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {DIAS.map((d) => (
                        <button
                          type="button"
                          key={d}
                          onClick={() =>
                            setModal({
                              ...modal,
                              data: { ...modal.data, dia_pago: d },
                            })
                          }
                          data-testid={`dia-${d}`}
                          className={`py-2.5 rounded-xl border-2 text-sm font-semibold transition-colors ${
                            Number(modal.data.dia_pago) === d
                              ? "border-med-primary bg-med-primary/10 text-med-primary"
                              : "border-med-border text-med-text hover:border-med-secondary"
                          }`}
                        >
                          Día {d}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="md:col-span-2">
                <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                  Notas
                </label>
                <textarea
                  rows={2}
                  value={modal.data.notas || ""}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, notas: e.target.value },
                    })
                  }
                  className="w-full rounded-xl bg-med-surface2 border border-transparent focus:border-med-primary focus:ring-2 focus:ring-med-primary/20 px-4 py-2.5 outline-none"
                />
              </div>

              {!modal.isEdit && (
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-med-muted mb-1.5">
                    Falleros vinculados{" "}
                    <span className="text-med-primary">*</span>
                    <span className="text-med-muted normal-case font-normal ml-2">
                      ({(modal.data.fallero_ids || []).length} seleccionados —
                      mínimo 1)
                    </span>
                  </label>
                  <div className="border border-med-border rounded-xl max-h-56 overflow-y-auto divide-y divide-med-border">
                    {falleros.length === 0 ? (
                      <div className="p-4 text-sm text-med-muted text-center">
                        No hay falleros creados todavía.
                      </div>
                    ) : (
                      falleros.map((f) => {
                        const checked = (modal.data.fallero_ids || []).includes(
                          f.id,
                        );
                        return (
                          <label
                            key={f.id}
                            className="flex items-center gap-3 px-4 py-2 hover:bg-med-surface2 cursor-pointer text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleFalleroInMandato(f.id)}
                              data-testid={`mandato-check-${f.id}`}
                              className="w-4 h-4 accent-med-primary"
                            />
                            <span className="flex-1 text-med-text">
                              {f.nombre} {f.apellidos}
                            </span>
                            {f.mandato_referencia && (
                              <span className="text-[10px] text-med-warning">
                                ya en {f.mandato_referencia}
                              </span>
                            )}
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
              {modal.isEdit && (
                <div className="md:col-span-2 border-t border-med-border pt-5">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-display text-lg font-bold text-med-text">
                      Falleros vinculados
                    </h4>
                  </div>

                  <div className="space-y-2 mb-4">
                    {mandatoFalleros.length === 0 ? (
                      <div className="text-sm text-med-muted bg-med-surface2 rounded-xl p-4 text-center">
                        No hay falleros vinculados.
                      </div>
                    ) : (
                      mandatoFalleros.map((f) => (
                        <div
                          key={f.id}
                          className="flex items-center justify-between rounded-xl bg-med-surface2 px-4 py-3"
                        >
                          <div>
                            <div className="font-medium text-med-text">
                              {f.nombre} {f.apellidos}
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await api.post(
                                  `/mandatos/${modal.data.id}/remove-fallero/${f.id}`,
                                );

                                setMandatoFalleros(
                                  mandatoFalleros.filter((x) => x.id !== f.id),
                                );
                                setFreeFalleros([...freeFalleros, f]);

                                toast.success("Fallero desvinculado");
                                fetchAll();
                              } catch {
                                toast.error("No se pudo desvincular");
                              }
                            }}
                            className="rounded-lg p-2 text-med-danger hover:bg-med-danger/10"
                          >
                            <Trash size={18} weight="duotone" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="flex gap-2">
                    <select
                      value={selectedFallero}
                      onChange={(e) => setSelectedFallero(e.target.value)}
                      className="flex-1 rounded-xl border border-med-border bg-med-surface2 px-4 py-2.5 outline-none focus:border-med-primary"
                    >
                      <option value="">Añadir fallero…</option>

                      {freeFalleros.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.nombre} {f.apellidos}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      disabled={!selectedFallero}
                      onClick={async () => {
                        try {
                          const fallero = freeFalleros.find(
                            (f) => f.id === selectedFallero,
                          );

                          await api.post(
                            `/mandatos/${modal.data.id}/add-fallero/${selectedFallero}`,
                          );

                          setMandatoFalleros([...mandatoFalleros, fallero]);
                          setFreeFalleros(
                            freeFalleros.filter(
                              (f) => f.id !== selectedFallero,
                            ),
                          );
                          setSelectedFallero("");

                          toast.success("Fallero añadido");
                          fetchAll();
                        } catch (err) {
                          toast.error(
                            err?.response?.data?.detail || "No se pudo añadir",
                          );
                        }
                      }}
                      className="rounded-xl bg-med-primary px-4 py-2 text-white disabled:opacity-40"
                    >
                      Añadir
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-med-surface border-t border-med-border px-6 py-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setModal(null)}
                className="px-5 py-2.5 rounded-xl border-2 border-med-border text-med-text hover:border-med-primary hover:text-med-primary font-medium"
              >
                Cancelar
              </button>
              <button
                type="submit"
                data-testid="save-mandato-btn"
                className="px-5 py-2.5 rounded-xl bg-med-primary hover:bg-med-primary-hover text-white font-semibold"
              >
                {modal.isEdit ? "Guardar" : "Crear mandato"}
              </button>
            </div>
          </motion.form>
        </div>
      )}
    </div>
  );
}
